import React, { useState, useMemo, forwardRef, useRef } from 'react';
import PropTypes from 'prop-types';
import SelectBase from 'react-select';

import InfoTooltip from 'components/InfoTooltip';

import SelectOption from './SelectOption';
import SelectSingleValue from './SelectSingleValue';
import SelectDropdownIndicator from './SelectDropdownIndicator';
import SelectLoadingIndicator from './SelectLoadingIndicator';

const Select = forwardRef(({
  label,
  required = false,
  tooltip,
  error,
  options,
  isMulti = false,
  className = '',
  selectClassName = '',
  onBlur,
  name,
  isReadOnly,
  ...props
}, ref) => {
  const [isFocused, setIsFocused] = useState(false);
  const [menuIsOpen, setMenuIsOpen] = useState(undefined);
  const internalRef = useRef(null);
  const selectRef = ref || internalRef;

  const focusSelect = () => {
    if (selectRef?.current) {
      selectRef.current.focus();
    }
  };

  const wrapperClasses = useMemo(() => {
    const base = `space-y-2 rounded-lg bg-slate-50 border transition-all duration-200 p-3 cursor-text ${isReadOnly ? 'pointer-events-none' : ''}`;

    if (error) {
      return `${base} ring-1 ${isFocused ? 'ring-red-400 border-red-400' : 'ring-red-300 border-red-300'} hover:border-red-400 hover:ring-red-400`;
    }

    if (isFocused) {
      return `${base} border-blue-400 ring-1`;
    }

    return `${base} border-slate-200 hover:border-gray-400`;
  }, [error, isFocused]);

  const selectStyles = {
    control: () => ({}),
    valueContainer: (base) => ({
      ...base,
      padding: 0,
    }),
    input: (base) => ({
      ...base,
      margin: 0,
      padding: 0,
    }),
    indicatorsContainer: (base) => ({
      ...base,
      padding: 0,
    }),
    menu: (base) => ({
      ...base,
      borderRadius: '0.5rem',
      overflow: 'hidden',
    }),
  };

  return (
    <div className={`flex flex-col gap-2 ${className}`}>
      <div className={wrapperClasses} onClick={focusSelect}>
        {label && (
          <div className="flex items-center gap-2">
            <label
              className={`
                block text-xs uppercase font-bold transition-all duration-200
                ${error ? 'text-red-500' : 'text-slate-500'}
              `}
            >
              {label}
              {required && <span className="text-red-500 ml-1">*</span>}
            </label>

            {tooltip && <InfoTooltip tooltip={tooltip} />}
          </div>
        )}

        <SelectBase
          ref={selectRef}
          options={options}
          isMulti={isMulti}
          unstyled
          styles={selectStyles}
          className={`text-sm ${selectClassName}`}
          menuIsOpen={menuIsOpen}
          onMenuOpen={() => setMenuIsOpen(true)}
          onMenuClose={() => setMenuIsOpen(false)}
          classNames={{
            control: () => 'flex items-center min-h-[22px]',
            placeholder: () => 'text-gray-400',
            menu: () => 'mt-2 bg-white border border-gray-200 shadow-lg rounded-lg',
            option: ({ isFocused, isSelected }) => `
              px-3 py-2 cursor-pointer text-sm
              transition-colors duration-150
              ${isSelected ? 'bg-blue-500 text-white' : 'text-slate-700'}
              ${!isSelected && isFocused ? 'bg-gray-100' : ''}
              ${!isSelected ? 'hover:bg-gray-100 focus:bg-gray-100 active:bg-gray-200' : 'hover:bg-blue-600 focus:bg-blue-600 active:bg-blue-700'}
            `,
            multiValue: () => 'bg-blue-100 text-blue-700 rounded px-2 py-0.5 mr-1',
            multiValueRemove: () => 'ml-1 hover:text-blue-900',
          }}
          components={{
            Option: SelectOption,
            SingleValue: SelectSingleValue,
            DropdownIndicator: isReadOnly ? null : SelectDropdownIndicator,
            LoadingIndicator: SelectLoadingIndicator,
            IndicatorSeparator: null,
          }}
          onFocus={() => setIsFocused(true)}
          onBlur={(e) => {
            onBlur && onBlur(e);
            setIsFocused(false);
          }}
          {...props}
        />
      </div>

      {error && error !== 'The input field is required' && <p className="text-xs text-red-500 px-2">{error}</p>}
    </div>
  );
});

Select.propTypes = {
  label: PropTypes.string.isRequired,
  options: PropTypes.array.isRequired,
  required: PropTypes.bool,
  tooltip: PropTypes.string,
  error: PropTypes.string,
  isMulti: PropTypes.bool,
  className: PropTypes.string,
  selectClassName: PropTypes.string,
};

Select.defaultProps = {
  required: false,
  tooltip: null,
  error: null,
  isMulti: false,
  className: '',
  selectClassName: '',
};

export default Select;
